// Tickets persona · detail (/tickets/:id) — round-3 depth (PLAN-W27 D / FRONTEND-BLUEPRINT §3.5).
// Full record over the generic GET /tickets/:id: the 5 priority axes + the trigger-computed
// composite score, the generic lifecycle workflow (the ONLY workflow the domain supports today —
// per-kind state machines live in counsel-gated admin_ticket_kind content that ships empty), the
// descriptive fields per the ticket schema, and the generic X3 audit/history panel
// (GET /tickets/:id/audit). Edit (title/body/axes) PATCHes the same detail route; everything is
// requirePermission-gated server-side (ticket.read / ticket.update / ticket.delete).
// NOTE (DSAR): the schema has NO ticket ↔ data_subject_request linkage (data_subject_request
// carries no ticket FK; ticket_dsr_extension holds DSR clocks per ticket but no DSAR-register FK)
// — deliberately NOT invented here; flagged as an owner design question.
import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { useI18n } from "../lib/i18n";
import { humanizeEnum } from "../lib/enum-labels";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  LifecycleBadge, scaleLabel, fmtDate, selectCls, TICKET_LIFECYCLES, PRIORITY_AXES, PRIORITY_SCALE, type TicketKind,
} from "./tickets";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);
const fmtDateTime = (v: unknown): string => (v ? new Date(String(v)).toLocaleString() : "—");

type Row = Record<string, unknown>;

export function TicketDetailRoute() {
  const { id } = useParams({ from: "/tickets/$id" });
  const { t } = useI18n();
  const qc = useQueryClient();
  const ticketQ = useQuery({ queryKey: ["ticket", id], queryFn: () => api.getEntity(TENANT_PATHS.tickets, id) });
  const kindsQ = useQuery({ queryKey: ["ticket-kinds"], queryFn: () => api.listTicketKinds() });
  const row = (ticketQ.data ?? null) as Row | null;
  const kinds = (kindsQ.data ?? []) as unknown as TicketKind[];
  const kind = kinds.find((k) => k.id === String(row?.ticketKindId)) ?? null;
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["ticket", id] });
    void qc.invalidateQueries({ queryKey: ["tickets"] });
    void qc.invalidateQueries({ queryKey: ["audit", TENANT_PATHS.tickets, id] });
  };
  const notFound = ticketQ.error instanceof ApiError && ticketQ.error.status === 404;

  return (
    <section>
      <Link to="/tickets" className="mb-4 inline-block text-sm text-primary hover:underline">
        ← {t("tk.detail.back")}
      </Link>
      <PageHeader
        title={String(row?.title ?? t("tk.detail.title"))}
        description={t("tk.detail.title")}
        actions={row ? (
          <Can perm="ticket.update"><EditTicketDialog id={id} row={row} onDone={invalidate} /></Can>
        ) : undefined}
      />

      {ticketQ.error && (
        <p className="text-sm text-destructive">
          {notFound ? t("tk.detail.notFound") : <>{t("tk.detail.loadFailed")} {errText(ticketQ.error)}</>}
        </p>
      )}

      <div className="grid gap-4">
        <WorkflowCard id={id} row={row} loading={ticketQ.isLoading} onDone={invalidate} />
        <PriorityCard row={row} loading={ticketQ.isLoading} />
        <FieldsCard row={row} loading={ticketQ.isLoading} kind={kind} />
        <HistoryCard id={id} />
      </div>
    </section>
  );
}

/** Status & workflow — the generic ticket lifecycle. Transition buttons PATCH { lifecycle } via the
 *  existing detail route (domain updateTicket); no per-kind state machine is invented (that content
 *  is counsel-gated and ships empty — honest note below). */
function WorkflowCard({ id, row, loading, onDone }: { id: string; row: Row | null; loading: boolean; onDone: () => void }) {
  const { t } = useI18n();
  const mut = useMutation({
    mutationFn: (lifecycle: string) => api.updateEntity(TENANT_PATHS.tickets, id, { lifecycle }),
    onSuccess: onDone,
  });
  const current = row ? String(row.lifecycle ?? "") : "";
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">{t("tk.detail.workflow")}</CardTitle>
        {!loading && row && <LifecycleBadge value={row.lifecycle} />}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-5 w-2/3" />
        ) : row ? (
          <div className="flex flex-col gap-3">
            <Can perm="ticket.update">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">{t("tk.detail.setStatus")}:</span>
                {TICKET_LIFECYCLES.filter((lc) => lc !== current).map((lc) => (
                  <Button key={lc} type="button" variant="outline" size="xs" disabled={mut.isPending} onClick={() => mut.mutate(lc)}>
                    {t(`tk.lc.${lc}`)}
                  </Button>
                ))}
              </div>
            </Can>
            {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
            {row.currentState != null && row.currentState !== "" && (
              <p className="text-xs text-muted-foreground">
                {t("tk.field.currentState")}: <span className="font-medium">{String(row.currentState)}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground">{t("tk.detail.workflowNote")}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** 5-axis priority (B-METHODOLOGY / AC-P3.9): the axes are captured at create/edit; the DB trigger
 *  recomputes composite_priority_score whenever an axis changes. */
function PriorityCard({ row, loading }: { row: Row | null; loading: boolean }) {
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">{t("tk.detail.priority")}</CardTitle>
        {!loading && row && (
          <Badge variant={row.compositePriorityScore == null ? "outline" : "default"} className="tabular-nums">
            {t("tk.detail.composite")}: {row.compositePriorityScore == null ? "—" : String(row.compositePriorityScore)}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-5 w-1/2" />)}</div>
        ) : row ? (
          <>
            <dl className="grid gap-3 sm:grid-cols-2">
              {PRIORITY_AXES.map((a) => (
                <div key={a}>
                  <dt className="text-xs text-muted-foreground">{t(`tk.axis.${a}`)}</dt>
                  <dd className="text-sm">
                    {row[a] == null || row[a] === ""
                      ? <span className="text-muted-foreground">{t("tk.axis.unset")}</span>
                      : <Badge variant="secondary">{scaleLabel(t, row[a])}</Badge>}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-xs text-muted-foreground">{t("tk.detail.compositeHint")}</p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Descriptive fields straight off the ticket row (B2 risk-operational §7). */
function FieldsCard({ row, loading, kind }: { row: Row | null; loading: boolean; kind: TicketKind | null }) {
  const { t } = useI18n();
  const fields: { label: string; value: ReactNode }[] = row
    ? [
        {
          label: t("tk.field.kind"),
          value: kind
            ? <Badge variant="secondary">{kind.displayName}</Badge>
            : <span className="font-mono text-xs text-muted-foreground" title={String(row.ticketKindId ?? "")}>{String(row.ticketKindId ?? "—").slice(0, 8)}…</span>,
        },
        ...(row.ticketTypeId ? [{ label: t("tk.field.type"), value: <span className="font-mono text-xs text-muted-foreground">{String(row.ticketTypeId)}</span> }] : []),
        ...(row.verwerkingId
          ? [{
              label: t("tk.field.verwerking"),
              value: (
                <Link to="/verwerkingen/$id" params={{ id: String(row.verwerkingId) }} className="text-sm text-primary hover:underline">
                  {String(row.verwerkingId).slice(0, 8)}…
                </Link>
              ),
            }]
          : []),
        ...(row.sourceKind ? [{ label: t("tk.field.source"), value: <span>{humanizeEnum(row.sourceKind)}{row.sourceId ? ` · ${String(row.sourceId).slice(0, 8)}…` : ""}</span> }] : []),
        ...(row.assignedUserId ? [{ label: t("tk.field.assigned"), value: <span className="font-mono text-xs">{String(row.assignedUserId)}</span> }] : []),
        { label: t("tk.field.language"), value: String(row.language ?? "—") },
        { label: t("tk.field.created"), value: fmtDateTime(row.createdAt) },
        { label: t("tk.field.due"), value: fmtDate(row.dueAt) },
        ...(row.resolvedAt ? [{ label: t("tk.field.resolved"), value: fmtDateTime(row.resolvedAt) }] : []),
      ]
    : [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("tk.detail.fields")}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-5 w-1/2" />)}</div>
        ) : row ? (
          <dl className="grid gap-3 sm:grid-cols-2">
            {row.body ? (
              <div className="sm:col-span-2">
                <dt className="text-xs text-muted-foreground">{t("tk.field.body")}</dt>
                <dd className="whitespace-pre-wrap text-sm">{String(row.body)}</dd>
              </div>
            ) : null}
            {fields.map((f) => (
              <div key={f.label}>
                <dt className="text-xs text-muted-foreground">{f.label}</dt>
                <dd className="text-sm">{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Generic per-entity change history (X3): GET /tickets/:id/audit → audit_log events. */
function HistoryCard({ id }: { id: string }) {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["audit", TENANT_PATHS.tickets, id],
    queryFn: () => api.listEntityAudit(TENANT_PATHS.tickets, id),
  });
  const rows = (data ?? []) as Row[];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("crud.history")}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-5 w-1/2" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("tk.detail.noHistory")}</p>
        ) : (
          <ul className="space-y-1">
            {rows.map((r) => (
              <li key={String(r.id)} className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{humanizeEnum(r.actionKind)}</span> · {fmtDateTime(r.occurredAt)}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Edit dialog — title/body + the 5 axes (the fields domain updateTicket supports besides the
 *  lifecycle, which has its own workflow buttons). Sends only set values: an unset axis stays
 *  unset rather than being forced through the biv_scale_5 enum as "". */
function EditTicketDialog({ id, row, onDone }: { id: string; row: Row; onDone: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const initial = (): Record<string, string> => ({
    title: row.title != null ? String(row.title) : "",
    body: row.body != null ? String(row.body) : "",
    ...Object.fromEntries(PRIORITY_AXES.map((a) => [a, row[a] != null ? String(row[a]) : ""])),
  });
  const [f, setF] = useState<Record<string, string>>(initial);
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  const mut = useMutation({
    mutationFn: () => api.updateEntity(TENANT_PATHS.tickets, id, {
      title: f.title!.trim(),
      body: f.body ?? "",
      ...Object.fromEntries(PRIORITY_AXES.map((a) => [a, f[a]]).filter(([, v]) => v)),
    }),
    onSuccess: () => { setOpen(false); onDone(); },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(initial()); else mut.reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">{t("crud.edit")}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("tk.detail.edit")}</DialogTitle>
          <DialogDescription>id {id}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="tke-title">{t("tk.col.title")} *</Label>
            <Input id="tke-title" value={f.title ?? ""} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tke-body">{t("tk.field.body")}</Label>
            <Textarea id="tke-body" rows={4} value={f.body ?? ""} onChange={(e) => set("body", e.target.value)} />
          </div>
          {PRIORITY_AXES.map((a) => (
            <div key={a} className="grid gap-1.5">
              <Label htmlFor={`tke-${a}`}>{t(`tk.axis.${a}`)}</Label>
              <select id={`tke-${a}`} value={f[a] ?? ""} onChange={(e) => set(a, e.target.value)} className={`${selectCls} w-full`}>
                <option value="">{t("tk.axis.unset")}</option>
                {PRIORITY_SCALE.map((s) => <option key={s} value={s}>{scaleLabel(t, s)}</option>)}
              </select>
            </div>
          ))}
          {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); mut.reset(); }}>{t("common.cancel")}</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !f.title?.trim()}>
            {mut.isPending ? t("crud.saving") : t("crud.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
